import React, { useEffect, useState, useRef } from "react";
import EditMenu from "./editmenu";
import DeleteMenu from "./deletemenu";
import EditModalOpen from "./editModalOpen";
import Table from "./table";
import Filter from "./filter";
import CreateExhibitorModal from "./Createnew";
import ViewMenu from "./viewmenu";

const Exhibitorslist = () => {
  const [response, setResponse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openMenuIndex, setOpenMenuIndex] = useState(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0, openUpwards: false });
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editData, setEditData] = useState(null);

  const [formData, setFormData] = useState({
    eventName: "",
    productId: "",
    productVariantId: "",
    product_type: "",
    file: null,
  });

  const [error, setError] = useState("");
  const menuRef = useRef(null);

  const [filters, setFilters] = useState({ eventName: "" });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [productList, setProductList] = useState([]);

  const [openDropdowns, setOpenDropdowns] = useState({});
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewModalData, setViewModalData] = useState([]);

  // 📌 Fetch View Modal Data
  const openViewModal = async (id) => {
    try {
      const res = await fetch(`/api/v1/exhibitors-list/admin/get-exhibitors-list-records/${id}`);
      const data = await res.json();
      setViewModalData(data);
      setViewModalOpen(true);
    } catch (err) {
      console.error("Error fetching view data:", err);
      setViewModalData([]);
    }
  };

  const closeViewModal = () => {
    setViewModalOpen(false);
    setViewModalData([]);
  };

  // 📌 Fetch Exhibitors List
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({ page, pageSize });
        if (filters.eventName) params.append("eventName", filters.eventName);

        const res = await fetch(`/api/v1/exhibitors-list/admin/get-all-exhibitors-list?${params}`);
        const data = await res.json();

        setResponse(data);
        setTotalCount(data.pagination?.total || 0);
      } catch (err) {
        console.error("Error fetching exhibitors list:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [filters, page, pageSize]);

  // 📌 Fetch Product List
  useEffect(() => {
    fetch("/api/v1/select-product-list?page=1&limit=1000")
      .then((res) => res.json())
      .then((data) => {
        if (data.success && data.data) {
          setProductList(data.data);
        }
      })
      .catch((err) => console.error("Failed to fetch product list:", err));
  }, []);

  // 📌 Close menu when clicked outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setOpenMenuIndex(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const dataArray = Array.isArray(response?.data) ? [...response.data] : response?.data ? [response.data] : [];

  // 📌 Menu Toggle
  const toggleMenu = (index, event) => {
    if (openMenuIndex === index) {
      setOpenMenuIndex(null);
    } else {
      const rect = event.currentTarget.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const menuHeight = 50;
      const openUpwards = rect.bottom + menuHeight > viewportHeight;
      setMenuPosition({
        top: openUpwards ? rect.top - menuHeight : rect.bottom,
        left: rect.right - 150,
        openUpwards,
      });
      setOpenMenuIndex(index);
    }
  };

  // 📌 Edit Modal
  const openEditModal = (item) => {
    setEditData(item);
    setFormData({
      eventName: item.eventName || "",
      productId: item.productId || "",
      productVariantId: item.productVariantId || "",
      product_type: item.product_type || "",
      file: null,
    });
    setError("");
    setEditModalOpen(true);
    setOpenMenuIndex(null);
  };

  const closeEditModal = () => {
    setEditModalOpen(false);
    setEditData(null);
    setFormData({
      eventName: "",
      productId: "",
      productVariantId: "",
      product_type: "",
      file: null,
    });
    setError("");
  };

  // 📌 Handle Form Inputs
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    setFormData((prev) => ({ ...prev, file: e.target.files[0] }));
  };

  // 📌 Submit Edit
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!editData) return;

    const payload = new FormData();
    payload.append("event_name", formData.eventName);
    payload.append("productId", formData.productId);
    payload.append("productVariantId", formData.productVariantId);
    payload.append("product_type", formData.product_type);
    if (formData.file) payload.append("file", formData.file);

    try {
      const res = await fetch(`/api/v1/exhibitors-list/admin/update-exhibitors-list/${editData.id}`, {
        method: "PUT",
        body: payload,
      });
      const result = await res.json();

      if (res.ok) {
        setResponse((prev) => {
          if (!prev?.data) return prev;
          const updatedData = Array.isArray(prev.data) ? [...prev.data] : [prev.data];
          const index = updatedData.findIndex((i) => i.id === editData.id);
          if (index !== -1) updatedData[index] = { ...updatedData[index], ...result.data };
          return { ...prev, data: updatedData };
        });
        closeEditModal();
      } else {
        setError(result.error || "Failed to update exhibitors list.");
      }
    } catch {
      setError("Failed to update exhibitors list.");
    }
  };

  // 📌 Delete Exhibitor
  const handleDeleteClick = async (item, e) => {
    e.stopPropagation();
    if (!item?.id) return alert("Invalid item id");
    if (!window.confirm("Are you sure you want to delete this exhibitor?")) return;

    try {
      const res = await fetch(`/api/v1/exhibitors-list/admin/delete-exhibitors-list/${item.id}`, {
        method: "DELETE",
      });
      const result = await res.json();
      if (res.ok) {
        alert(result.message || "Exhibitor deleted successfully.");
        setResponse((prev) => ({
          ...prev,
          data: prev?.data?.filter((i) => i.id !== item.id) || [],
        }));
      } else {
        alert(result.error || "Failed to delete exhibitor.");
      }
    } catch {
      alert("Failed to delete exhibitor.");
    }
    setOpenMenuIndex(null);
  };

  // 📌 Filters & Pagination
  const handleFilterChange = (newFilters) => {
    setPage(1);
    setFilters(newFilters);
  };

  const handlePageChange = (newPage) => setPage(newPage);
  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setPage(1);
  };

  const handleCreateModalClose = (shouldRefresh = false) => {
    setCreateModalOpen(false);
    if (shouldRefresh) {
      setPage(1);
      setFilters({ ...filters });
    }
  };

  if (loading) return <div>Loading...</div>;
  if (!response?.data) return <div>No data found.</div>;

  // 📌 Variant Name Map
  const variantNameMap = {};
  productList.forEach((product) => {
    product.product_variants?.forEach((variant) => {
      variantNameMap[variant.variation_id] = variant.name;
    });
  });

  return (
    <div>
      <h2>{response.message}</h2>
      <Filter filters={filters} onFilterChange={handleFilterChange} onCreateNew={() => setCreateModalOpen(true)} />
      <Table
        dataArray={dataArray}
        variantNameMap={variantNameMap}
        openMenuIndex={openMenuIndex}
        menuPosition={menuPosition}
        toggleMenu={toggleMenu}
        openEditModal={openEditModal}
        handleDeleteClick={handleDeleteClick}
        menuRef={menuRef}
        EditMenu={EditMenu}
        DeleteMenu={DeleteMenu}
        page={page}
        pageSize={pageSize}
        totalCount={totalCount}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        openDropdowns={openDropdowns}
        toggleDropdown={(id) => setOpenDropdowns((prev) => ({ ...prev, [id]: !prev[id] }))}
        openViewModal={openViewModal}
      />
      <ViewMenu modalOpen={viewModalOpen} recordData={viewModalData} handleClose={closeViewModal} />
      <EditModalOpen
        editModalOpen={editModalOpen}
        closeEditModal={closeEditModal}
        formData={formData}
        handleInputChange={handleInputChange}
        handleFileChange={handleFileChange}
        handleSubmit={handleSubmit}
        error={error}
        productList={productList}
        variantNameMap={variantNameMap}
      />
      <CreateExhibitorModal open={createModalOpen} handleClose={handleCreateModalClose} productList={productList} />
    </div>
  );
};

export default Exhibitorslist;
